import { isEmpty, isUndefined } from 'lodash';
import { SagaReturnType, call, put, select, takeEvery } from 'redux-saga/effects';
import {
  doFailedCancelAccountApplicationDocument,
  doFailedDeleteAccountApplicationDocument,
  doFailedGetAccountApplication,
  doSetAccountApplicationCategoryViewList,
  doSucceededCancelAccountApplicationDocument,
  doSucceededDeleteAccountApplicationDocument,
  doSucceededGetAccountApplication,
} from 'src/actions';
import { State, Type } from 'src/actions/utils';
import { getClientApi, type AccountApplicationApi } from 'src/data-communication';
import { mapAccountApplicationDtoToModel } from 'src/mappers/accountApplication.mapper';
import { AccountApplicationCategory, AccountApplicationCategoryView } from 'src/models';
import { TReduxAction } from 'src/typings/commonTypes';
import { getPayloadFromErrorResponse } from 'src/utils';

const clientApi = getClientApi();

function* getAccountApplication(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const { id, accountId } = action.payload.params;

    const result: SagaReturnType<AccountApplicationApi['retrieve']> = yield call(
      clientApi.accountApplication.retrieve,
      {
        params: { id, accountId },
        authToken,
      },
    );

    const model = mapAccountApplicationDtoToModel(result);

    const categoryViewList = model.categories.map((category: AccountApplicationCategory) => {
      if (category.name.isGovernmentId) {
        if (isEmpty(category.uploadedDocuments)) {
          return {
            key: `${category.name.value}`,
            name: category.name,
            isResolved: category.isResolved,
            requestedDocuments: category.requestedDocuments,
            totalRequestedDocuments: category.totalRequestedDocuments,
            uploadedDocuments: category.uploadedDocuments,
            adminNote: category.adminNote,
            children: [
              {
                key: `${category.name.value}-1`,
                name: category.name,
                isResolved: category.isResolved,
                requestedDocuments: category.requestedDocuments,
                uploadedDocuments: [],
              },
            ],
          };
        }

        const children =
          category.uploadedDocuments?.map((document, index) => ({
            key: `${category.name.value}-${index + 1}`,
            name: category.name,
            isResolved: true,
            requestedDocuments: category.requestedDocuments,
            uploadedDocuments: [document],
          })) || [];

        if (
          !category.isResolved &&
          !isUndefined(category.totalRequestedDocuments) &&
          children.length < category.totalRequestedDocuments
        ) {
          for (let index = children.length; index < category.totalRequestedDocuments; index++) {
            children.push({
              key: `${category.name.value}-${index + 1}`,
              name: category.name,
              isResolved: false,
              requestedDocuments: category.requestedDocuments,
              uploadedDocuments: [],
            });
          }
        }

        return {
          key: `${category.name.value}`,
          name: category.name,
          isResolved: category.isResolved,
          requestedDocuments: category.requestedDocuments,
          uploadedDocuments: category.uploadedDocuments,
          adminNote: category.adminNote,
          totalRequestedDocuments: category.totalRequestedDocuments,
          children,
        };
      }

      return {
        key: category.name.value,
        name: category.name,
        isResolved: category.isResolved,
        requestedDocuments: category.requestedDocuments,
        totalRequestedDocuments: category.totalRequestedDocuments,
        uploadedDocuments: category.uploadedDocuments,
        adminNote: category.adminNote,
      };
    });
    yield put(doSetAccountApplicationCategoryViewList(categoryViewList));

    yield put(doSucceededGetAccountApplication(model));
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put(doFailedGetAccountApplication(payload));
  }
}

function* refreshAccountApplication(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const { id, accountId } = action.payload.params;
    const { key, documentId } = action.payload;

    const result: SagaReturnType<AccountApplicationApi['retrieve']> = yield call(
      clientApi.accountApplication.retrieve,
      {
        params: { id, accountId },
        authToken,
      },
    );

    const model = mapAccountApplicationDtoToModel(result);

    const currentCategoryViewList: AccountApplicationCategoryView[] = yield select(
      state => state.app.accountApplicationCategoryViewList,
    );

    const updatedCategoryViewList = model.categories.map(modelCategory => {
      const currentCategoryView = currentCategoryViewList.find(cat => cat.name.value === modelCategory.name.value);

      if (modelCategory?.name.isGovernmentId) {
        if (isEmpty(modelCategory.uploadedDocuments)) {
          return {
            key: `${modelCategory.name.value}`,
            name: modelCategory.name,
            isResolved: modelCategory.isResolved,
            requestedDocuments: modelCategory.requestedDocuments,
            totalRequestedDocuments: modelCategory.totalRequestedDocuments,
            uploadedDocuments: [],
            adminNote: modelCategory.adminNote,
            children: [
              {
                key: `${modelCategory.name.value}-1`,
                name: modelCategory.name,
                isResolved: modelCategory.isResolved,
                requestedDocuments: modelCategory.requestedDocuments,
                uploadedDocuments: [],
              },
            ],
          };
        }

        return {
          key: `${modelCategory.name.value}`,
          name: modelCategory.name,
          isResolved: modelCategory.isResolved,
          requestedDocuments: modelCategory.requestedDocuments,
          totalRequestedDocuments: modelCategory.totalRequestedDocuments,
          uploadedDocuments: modelCategory.uploadedDocuments,
          adminNote: modelCategory.adminNote,
          children:
            currentCategoryView?.children?.map(child => {
              const uploadedDocument = model.uploadedDocuments.find(uploadedDoc => uploadedDoc.id === documentId);

              if (child.key === key && uploadedDocument) {
                return {
                  ...child,
                  isResolved: true,
                  requestedDocuments: modelCategory.requestedDocuments,
                  uploadedDocuments: uploadedDocument ? [uploadedDocument] : [],
                };
              }

              return child;
            }) || [],
        };
      }

      return {
        key: modelCategory.name.value,
        name: modelCategory.name,
        isResolved: modelCategory.isResolved,
        requestedDocuments: modelCategory.requestedDocuments,
        uploadedDocuments: modelCategory.uploadedDocuments,
        adminNote: modelCategory.adminNote,
      };
    });
    yield put(doSetAccountApplicationCategoryViewList(updatedCategoryViewList));

    yield put(doSucceededGetAccountApplication(model));
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put(doFailedGetAccountApplication(payload));
  }
}

function* deleteAccountApplicationDocument(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const { id, accountId, documentId } = action.payload.params;
    const { key } = action.payload;

    yield call(clientApi.accountApplication.deleteDocument, {
      params: { id, accountId, documentId },
      authToken,
    });

    const result: SagaReturnType<AccountApplicationApi['retrieve']> = yield call(
      clientApi.accountApplication.retrieve,
      {
        params: { id, accountId },
        authToken,
      },
    );

    const currentCategoryViewList: AccountApplicationCategoryView[] = yield select(
      state => state.app.accountApplicationCategoryViewList,
    );

    const currentCategoryView = currentCategoryViewList.find(
      cat => cat.key === key || cat.children?.some(child => child.key === key),
    );

    const model = mapAccountApplicationDtoToModel(result);

    const updatedCategory = model.categories.find(cat => cat.name.value === currentCategoryView?.name.value);

    const updatedCategoryViewList = currentCategoryViewList.map(cat => {
      if (cat.name.value === updatedCategory?.name.value) {
        if (cat.name.isGovernmentId) {
          const newChildren = cat.children
            ?.filter(child => !child.uploadedDocuments?.find(doc => doc.id === documentId))
            .map((child, index) => ({
              ...child,
              isResolved: Array.isArray(child.uploadedDocuments) ? child.uploadedDocuments?.length > 0 : false,
              requestedDocuments: updatedCategory.requestedDocuments,
              uploadedDocuments: child.uploadedDocuments,
              key: `${cat.name.value}-${index + 1}`,
            }));

          if (isEmpty(newChildren)) {
            newChildren?.push({
              key: `${cat.name.value}-1`,
              name: cat.name,
              isResolved: cat.isResolved,
              requestedDocuments: cat.requestedDocuments,
              uploadedDocuments: [],
            });
          }

          return {
            ...cat,
            isResolved: updatedCategory.isResolved,
            uploadedDocuments: updatedCategory.uploadedDocuments,
            requestedDocuments: updatedCategory.requestedDocuments,
            totalRequestedDocuments: updatedCategory.totalRequestedDocuments,
            adminNote: updatedCategory.adminNote,
            children: newChildren,
          };
        }

        return {
          ...cat,
          isResolved: updatedCategory.isResolved,
          uploadedDocuments: updatedCategory.uploadedDocuments,
          requestedDocuments: updatedCategory.requestedDocuments,
          totalRequestedDocuments: updatedCategory.totalRequestedDocuments,
          adminNote: updatedCategory.adminNote,
        };
      }

      return cat;
    });
    yield put(doSetAccountApplicationCategoryViewList(updatedCategoryViewList));

    yield put(doSucceededGetAccountApplication(model));

    yield put(doSucceededDeleteAccountApplicationDocument(action.payload));
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put(doFailedDeleteAccountApplicationDocument(payload));
  }
}

function* cancelAccountApplicationDocument(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const { id, accountId, documentId } = action.payload.params;
    const { key } = action.payload;

    yield call(clientApi.accountApplication.deleteDocument, {
      params: { id, accountId, documentId },
      authToken,
    });

    const result: SagaReturnType<AccountApplicationApi['retrieve']> = yield call(
      clientApi.accountApplication.retrieve,
      {
        params: { id, accountId },
        authToken,
      },
    );

    const currentCategoryViewList: AccountApplicationCategoryView[] = yield select(
      state => state.app.accountApplicationCategoryViewList,
    );

    const currentCategoryView = currentCategoryViewList.find(
      cat => cat.key === key || cat.children?.some(child => child.key === key),
    );

    const model = mapAccountApplicationDtoToModel(result);

    const updatedCategory = model.categories.find(cat => cat.name.value === currentCategoryView?.name.value);

    const updatedCategoryViewList = currentCategoryViewList.map(cat => {
      if (cat.name.value === updatedCategory?.name.value) {
        if (cat.name.isGovernmentId) {
          return {
            ...cat,
            isResolved: updatedCategory.isResolved,
            uploadedDocuments: updatedCategory.uploadedDocuments,
            requestedDocuments: updatedCategory.requestedDocuments,
            totalRequestedDocuments: updatedCategory.totalRequestedDocuments,
            adminNote: updatedCategory.adminNote,
            children: cat.children?.map(child => {
              if (child.key === key) {
                return {
                  ...child,
                  isResolved: Array.isArray(child.uploadedDocuments) ? child.uploadedDocuments?.length > 0 : false,
                  requestedDocuments: updatedCategory.requestedDocuments,
                  uploadedDocuments: child.uploadedDocuments?.filter(doc => !(doc.id === documentId)),
                };
              }

              return child;
            }),
          };
        }

        return {
          ...cat,
          isResolved: updatedCategory.isResolved,
          uploadedDocuments: updatedCategory.uploadedDocuments,
          requestedDocuments: updatedCategory.requestedDocuments,
          totalRequestedDocuments: updatedCategory.totalRequestedDocuments,
          adminNote: updatedCategory.adminNote,
        };
      }

      return cat;
    });

    yield put(doSetAccountApplicationCategoryViewList(updatedCategoryViewList));

    yield put(doSucceededGetAccountApplication(model));

    yield put(doSucceededCancelAccountApplicationDocument(action.payload));
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put(doFailedCancelAccountApplicationDocument(payload));
  }
}

export function* registerAccountApplicationSagas() {
  yield takeEvery(State.actionRequested(Type.GET_ACCOUNT_APPLICATION), getAccountApplication);
  yield takeEvery(Type.REFRESH_ACCOUNT_APPLICATION, refreshAccountApplication);
  yield takeEvery(State.actionRequested(Type.DELETE_ACCOUNT_APPLICATION_DOCUMENT), deleteAccountApplicationDocument);
  yield takeEvery(State.actionRequested(Type.CANCEL_ACCOUNT_APPLICATION_DOCUMENT), cancelAccountApplicationDocument);
}
